import React, { useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, IconButton, Typography, alpha, useTheme } from "@mui/material";

export interface CopyBlockProps {
  content: string;
  label?: string;
  language?: string;
}

export function CopyBlock({
  content,
  label,
}: CopyBlockProps): React.ReactElement {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API might not be available
    }
  };

  return (
    <Box>
      {label && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 0.5, display: "block" }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          position: "relative",
          bgcolor: alpha(theme.palette.common.black, 0.04),
          borderRadius: 1,
          p: 2,
          pr: 5,
          border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        }}
      >
        <Typography
          component="pre"
          variant="caption"
          fontFamily="monospace"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {content}
        </Typography>
        <IconButton
          size="small"
          onClick={handleCopy}
          aria-label="Copy to clipboard"
          sx={{ position: "absolute", top: 4, right: 4 }}
        >
          {copied ? (
            <CheckIcon fontSize="small" color="success" />
          ) : (
            <ContentCopyIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Box>
  );
}
